import referralService from './referralService';
import { ACQUISITION_PARAM_KEYS, captureAcquisitionSession } from './acquisitionSession';

const normalizeString = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const touchFields = ['landing_path', 'captured_at', 'referrer', ...ACQUISITION_PARAM_KEYS];

const flattenTouch = (prefix, touch) => Object.fromEntries(
  touchFields
    .map((key) => [`${prefix}_${key}`, normalizeString(touch?.[key])])
    .filter(([, value]) => Boolean(value))
);

const getLocation = (override) => override ||
  (typeof window !== 'undefined' && window.location) || { pathname: '/', search: '' };

export function buildAcquisitionMetadata(options = {}) {
  const location = getLocation(options.location);
  const session = options.sessionSnapshot || captureAcquisitionSession({
    location,
    storage: options.sessionStorage,
    capturedAt: options.capturedAt,
    referrer: options.referrer,
    persistReferral: options.persistReferral,
  });
  const firstTouch = session?.first_touch || session || {};
  const lastTouch = session?.last_touch || session || {};
  const storedReferralCapture = options.referralCapture ||
    referralService.getStoredReferralCapture?.();
  const referralCode = normalizeString(options.referralCode) || normalizeString(lastTouch.ref) ||
    normalizeString(firstTouch.ref) || normalizeString(storedReferralCapture?.code) ||
    normalizeString(referralService.getStoredReferralCode());
  const normalizedReferralCode = referralCode?.toUpperCase();
  const storedCaptureMatches = normalizeString(storedReferralCapture?.code)?.toUpperCase() ===
    normalizedReferralCode;
  const lastTouchMatches = normalizeString(lastTouch.ref)?.toUpperCase() ===
    normalizedReferralCode;
  const firstTouchMatches = normalizeString(firstTouch.ref)?.toUpperCase() ===
    normalizedReferralCode;
  const referralCapturedAt = storedCaptureMatches
    ? normalizeString(storedReferralCapture?.capturedAt)
    : lastTouchMatches
      ? normalizeString(lastTouch.captured_at)
      : firstTouchMatches
        ? normalizeString(firstTouch.captured_at)
        : null;
  const canonical = Object.fromEntries(
    ACQUISITION_PARAM_KEYS
      .filter((key) => key !== 'ref')
      .map((key) => [key, normalizeString(lastTouch[key]) || normalizeString(firstTouch[key])])
      .filter(([, value]) => Boolean(value))
  );
  const hasCampaign = Object.keys(canonical).some((key) => key.startsWith('utm_'));

  return {
    channel: referralCode ? 'referral' : canonical.utm_medium || (hasCampaign ? 'campaign' : 'direct'),
    ...(normalizeString(session?.acquisition_session_id)
      ? { acquisition_session_id: normalizeString(session.acquisition_session_id) }
      : {}),
    ...(referralCode ? { referral_code: referralCode } : {}),
    ...(referralCapturedAt ? { referral_captured_at: referralCapturedAt } : {}),
    ...canonical,
    ...(normalizeString(lastTouch.referrer) ? { referrer: normalizeString(lastTouch.referrer) } : {}),
    landing_path: normalizeString(firstTouch.landing_path) || `${location.pathname || '/'}${location.search || ''}`,
    captured_at: normalizeString(firstTouch.captured_at) || options.capturedAt || new Date().toISOString(),
    ...flattenTouch('first_touch', firstTouch),
    ...flattenTouch('last_touch', lastTouch),
  };
}

export function appendAcquisitionMetadata(payload, options = {}) {
  return {
    ...payload,
    metadata: { ...(payload.metadata || {}), ...buildAcquisitionMetadata(options) },
  };
}
